import React, { useEffect, useMemo, useRef, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './RoleManagement.styles';
import Content from 'studio/components/Content/Content';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import TextField from 'studio/components/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import { trackEvent } from 'utils/eventTracking';
import _ from 'lodash';
import SecurityIcon from '@mui/icons-material/Security';
import PageTitle from 'studio/components/PageTitle';
import EditIcon from '@mui/icons-material/EditRounded';
import TextArea from 'components/v3/TextArea/TextArea';
import OverflowableContent from 'studio/components/OverflowableContent/OverflowableContent';
import UserChip from 'studio/components/Chip/UserChip/UserChip';
import { Add } from '@mui/icons-material';
import DialogContentText from '@mui/material/DialogContentText';
import Link from '@mui/material/Link';
import { veezooUrls } from 'config/constants';
import { paths } from 'studio/App/Admin/Admin';
import Progress from 'studio/components/Progress';
import { sortBy } from 'utils/arrayUtils';
import { pluralize as plrl } from 'studio/helpers';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import UserSelector from 'studio/components/UserSelector/UserSelector';
import { fetchAllRoles, fetchAllUsers, fetchRoleUsers } from 'utils/userRoleUtils';

// the free version of data grid limits the number of rows per page to at most 100
const pageSize = 100;

const RoleManagement = ({ classes }) => {
  useEffect(() => {
    trackEvent('Admin Roles Opened');
  }, []);

  const rolesColumns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    {
      field: 'users',
      headerName: 'Users',
      flex: 3,
      renderCell: params => renderUsersCell(params.value, 'Users')
    }
  ];

  // references the content element
  const contentRef = useRef(null);
  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  // the roles
  const [roles, setRoles] = useState(undefined);
  // contains, indexed by role ID, the IDs of the users that are assigned to the roles
  const [userIdsForRoleId, setUserIdsForRoleId] = useState({});
  // all users of the organization
  const [allUsers, setAllUsers] = useState(null);
  // the SAML configuration, initialized to 'undefined' while it is loading
  const [samlConfiguration, setSamlConfiguration] = useState(undefined);
  // the roles that are selected in the user table
  const [selectedRoles, setSelectedRoles] = useState([]);
  // specifies whether to show the dialog to create or edit a role
  const [showCreateOrEditDialog, setShowCreateOrEditDialog] = useState(false);
  // whether the dialog is for creating or editing, will steer labels and actions
  const [isCreateDialog, setIsCreateDialog] = useState(true);
  // the value of the role name field in the dialog
  const [roleName, setRoleName] = useState(null);
  // the value of the role description field in the dialog
  const [roleDescription, setRoleDescription] = useState(null);
  // the users of the role in the dialog
  const [users, setUsers] = useState([]);

  // the rows of the roles table
  const roleRows = (roles || []).map(role => ({
    id: role.id,
    name: role.name,
    description: role.description,
    users: userIdsForRoleId[role.id]
  }));

  // specifies if the "Invite" button in the dialog is enabled
  const createOrEditButtonIsEnabled = useMemo(
    () =>
      // enable the button if there is a role name
      roleName?.length > 0 && !loading,
    [roleName, loading]
  );

  // specifies if SAML is being used for authentication
  const isSamlAuthentication = useMemo(() => samlConfiguration?.data.isEnabled, [samlConfiguration]);
  const getUsersFromIds = usersIds => {
    return usersIds.map(userId => allUsers.find(user => user.id === userId));
  };
  // opens the create or edit dialog and initializes the fields
  const createOrEditRole = selectedRoleId => {
    // if a role id was passed, it means that the dialog is for editing otherwise it is for creating
    if (selectedRoleId) {
      const selectedRole = roles.find(role => role.id === selectedRoleId);
      setIsCreateDialog(false);
      setRoleName(selectedRole.name);
      setRoleDescription(selectedRole.description);
      setUsers(getUsersFromIds(userIdsForRoleId[selectedRoleId]));
    } else {
      setIsCreateDialog(true);
      setRoleName('');
      setRoleDescription('');
      setUsers([]);
    }
    setShowCreateOrEditDialog(true);
  };

  const getAllUsers = async () => {
    const users = await fetchAllUsers(withLoader, errorMessage);
    setAllUsers(users);
  };

  const getRoles = async () => {
    const roles = await fetchAllRoles(withLoader, errorMessage);
    setRoles(roles);
    return roles;
  };

  const getRoleUsers = async role => {
    const userIds = await fetchRoleUsers(role.id, withLoader, errorMessage);
    setUserIdsForRoleId(roleUsers => ({ ...roleUsers, [role.id]: userIds }));
  };

  const getSamlConfiguration = async () => {
    const response = await services.getSamlConfiguration();
    const samlConfiguration = response.data || null;
    setSamlConfiguration(samlConfiguration);
  };

  const createRoleAndAssignUsers = async () => {
    const response = await withLoader(services.createRole(roleName, roleDescription));
    if (response.success) {
      const newRole = response.data.data;
      const userIds = users.map(u => u.id);
      // assign users to the role
      const assignmentResponse = await withLoader(services.assignUsersToRole(newRole.id, userIds));
      if (assignmentResponse.success) {
        setRoles([...roles, newRole]);
        successMessage('Role created');
        return newRole;
      } else {
        const message =
          assignmentResponse?.response?.data?.data?.message || 'There was an error assigning the users to the role';
        errorMessage(message);
      }
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error creating the role';
      errorMessage(message);
    }
  };

  const editRoleAndAssignUsers = async roleId => {
    const response = await withLoader(services.updateRole(roleId, roleName, roleDescription));
    const userIds = users.map(u => u.id);
    const assignmentResponse = await withLoader(services.assignUsersToRole(roleId, userIds));
    if (response.success && assignmentResponse.success) {
      const updatedRole = response.data.data;
      setRoles(roles.map(role => (role.id === roleId ? updatedRole : role)));
      successMessage('Role updated');
      return updatedRole;
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error updating the role';
      errorMessage(message);
    }
  };

  const deleteRoles = async roleIds => {
    const responses = await withLoader(Promise.all(roleIds.map(roleId => services.deleteRole(roleId))));
    const [successResponses, errorResponses] = _.partition(responses, response => response.success);
    // show success and/or error messages
    if (successResponses.length > 0) {
      // show different messages for single / multiple deletions
      successMessage(
        `The ${plrl(roleIds.length, 'role')} ${plrl(roleIds.length, 'has', 'have')} successfully been deleted`
      );
    }
    if (errorResponses.length > 0) {
      errorResponses.forEach(response => {
        const message =
          response?.data?.data?.message || `There was an error deleting the ${plrl(roleIds.length, 'role')}`;
        errorMessage(message);
      });
    }
  };

  // retrieves the users and SAML configuration
  useEffect(() => {
    getRoles().then(roles => roles.forEach(role => getRoleUsers(role)));
    getAllUsers();
    getSamlConfiguration();
  }, []);

  // the create action for the data grids
  const createAction = {
    label: 'Create',
    icon: <Add />,
    tooltip: 'Create a new user role',
    disabled: loading,
    onClick: () => createOrEditRole(undefined)
  };

  const editAction = {
    label: 'Edit',
    icon: <EditIcon />,
    tooltip: selectedRoles.length === 1 ? 'Edit the selected role' : 'Select a single role to edit',
    disabled: selectedRoles.length !== 1,
    onClick: () => createOrEditRole(selectedRoles[0])
  };

  // the "delete" action for the data grids
  const deleteAction = selectedRoles => ({
    label: 'Delete',
    icon: <DeleteIcon />,
    tooltip: selectedRoles.length > 0 ? `Delete the selected roles` : `Select one or more role to delete`,
    color: 'secondary',
    disabled: selectedRoles.length === 0,
    onClick: () =>
      showConfirmationModal(() => {
        const count = selectedRoles.length;
        return {
          title: `Delete ${plrl(count, 'role')}`,
          content:
            count === 1 ? `Do you really want to delete the role?` : `Do you really want to delete ${count} roles?`,
          confirmButtonText: 'Delete',
          onConfirm: async () => {
            await deleteRoles(selectedRoles);
            await getRoles();
            setSelectedRoles([]);
          }
        };
      })
  });

  // renders a data grid cell containing users
  const renderUsersCell = (roleUserIds, overflowPopperTitle) =>
    roleUserIds ? (
      <OverflowableContent
        className={classes.roleUsers}
        tooltip={`Show all ${overflowPopperTitle.toLowerCase()}`}
        popperTitle={overflowPopperTitle}
        popperBoundariesElementRef={contentRef}
        content={isPopper =>
          sortBy(
            roleUserIds?.map(userId => {
              const user = allUsers?.find(user => user.id === userId);
              return [userId, user];
            }),
            ([userId, user]) => (user ? `${user.firstName} ${user.lastName}` : userId).toLowerCase()
          )?.map(([userId, user]) => (
            <UserChip
              key={userId}
              userId={userId}
              user={user}
              // display the tooltip to the left when inside the popper for more readability
              tooltipPlacement={isPopper && 'left'}
            />
          ))
        }
      />
    ) : (
      <Progress size={16} />
    );

  return (
    <Content loading={!roles || !allUsers || samlConfiguration === undefined} ref={contentRef} data-test="rolesContent">
      <PageTitle icon={SecurityIcon}>Roles</PageTitle>
      <Typography variant="body1" gutterBottom component="div">
        Roles can be assigned to users to control their access to Veezoo. For example, you can use roles to:
        <ul>
          <li>Control which knowledge graphs can be accessed</li>
          <li>Share dashboards with specific users</li>
          <li>Restrict access to certain concepts within a knowledge graph (column-level security)</li>
          <li>Implement row-level security</li>
          <li>
            and more: check our{' '}
            <Link href={veezooUrls.roleDocs} target="_blank" rel="noreferrer">
              documentation
            </Link>{' '}
            for more details
          </li>
        </ul>
      </Typography>
      <DataGrid
        className={classes.rolesDatagrid}
        pageSize={pageSize}
        rows={roleRows}
        columns={rolesColumns.map(column => ({ ...column, disableClickEventBubbling: true }))}
        checkboxSelection
        selectionModel={selectedRoles}
        onSelectionModelChange={newSelection => {
          const selection = newSelection.selectionModel || newSelection;
          setSelectedRoles(selection);
        }}
        sortModel={[{ field: 'name', sort: 'asc' }]}
        actions={[createAction, editAction, deleteAction(selectedRoles)]}
        noRowsText="There are no roles yet"
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={showCreateOrEditDialog}
        onClose={_ => setShowCreateOrEditDialog(false)}
        data-test="roleFormDialog"
      >
        <DialogTitle>{isCreateDialog ? 'Create Role' : 'Edit Role'}</DialogTitle>
        <DialogContent>
          Enter the role information below.
          <TextField
            className={classes.marginTop}
            value={roleName}
            label="Name"
            description="This is the name that you'll use in VKL to refer to this role."
            onChange={event => setRoleName(event.target.value)}
            autoFocus
            data-test="name"
          />
          <TextArea
            className={classes.marginTop}
            value={roleDescription}
            label="Description"
            placeholder="Enter a description for this role"
            onChange={event => setRoleDescription(event.target.value)}
            data-test="description"
          />
          {isSamlAuthentication && (
            <DialogContentText className={classes.marginTop}>
              When Single Sign-On is enabled, users can be assigned to roles via the mappings defined in the{' '}
              <Link href={paths.sso} target="_blank" rel="noreferrer">
                Single Sign-On
              </Link>{' '}
              section.
            </DialogContentText>
          )}
          {!isSamlAuthentication && (
            <div className={classes.usersContainer}>
              <UserSelector
                multiple
                value={users}
                onChange={(_, value) => setUsers(value)}
                label="Users"
                description="Select the users that should be assigned to this role."
                users={allUsers}
                disabled={loading}
                data-test="users"
              />
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.selectAllButton}
                  disabled={loading}
                  onClick={() => setUsers(allUsers)}
                >
                  Select all
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" disabled={loading} onClick={_ => setShowCreateOrEditDialog(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={async () => {
              const role = await (isCreateDialog
                ? createRoleAndAssignUsers()
                : editRoleAndAssignUsers(selectedRoles[0]));
              if (role) {
                await getRoleUsers(role);
              }
              setSelectedRoles([]);
              setShowCreateOrEditDialog(false);
            }}
            disabled={!createOrEditButtonIsEnabled}
          >
            {isCreateDialog ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};

export default withStyles(styles)(RoleManagement);
